import { apiConstants } from "@data/constants/apiConstants"
import { authConstants } from "@data/constants/authConstants"
import { liveModeConstants } from "@data/constants/liveModeConstants"
import { authService } from "@data/services/authService"

export const TUTOR_EVENTS_PANEL_ACTIONS = {
    DISMISS_EVENT: "TUTOR_EVENTS_PANEL/DISMISS_EVENT",
    CLEAR_DISMISSED: "TUTOR_EVENTS_PANEL/CLEAR_DISMISSED",
    SYNC_STARTED_AT: "TUTOR_EVENTS_PANEL/SYNC_STARTED_AT",
    MARK_SAM_SENT: "TUTOR_EVENTS_PANEL/MARK_SAM_SENT",
}

const initialState = {
    dismissedEventHashes: [],
    // `${type}-${studentId}` -> timestamp the event was first seen.
    startedAtByKey: {},
    // `${type}-${studentId}` -> timestamp the TUTOR sent Sam for that event.
    // Used to re-base the escalation clock on the Sam-send (not the original
    // event time) and to tell tutor-initiated Sam apart from a student-initiated
    // checkpoint. Persisted so it survives a page refresh mid-session.
    samSentAtByKey: {},
}

const MAX_DISMISSED_HASHES = 200

export function tutorEventsPanel(state = initialState, action) {
    switch (action.type) {
        case authConstants.LOGOUT_401:
        case authConstants.RESET_AUTH_STATE:
        case authService.logout.name + apiConstants._SUCCESS:
            return initialState

        // Drop the per-session timing caches when the live session ends so the
        // persisted store doesn't accumulate stale `${type}-${studentId}` keys
        // across sessions. Dismissed hashes survive (they're scoped per event
        // lifetime via the startedAt suffix and capped at MAX_DISMISSED_HASHES).
        case liveModeConstants.RESET_LIVE_MODE:
            return { ...state, startedAtByKey: {}, samSentAtByKey: {} }

        case TUTOR_EVENTS_PANEL_ACTIONS.DISMISS_EVENT: {
            const hash = action.payload?.hash
            if (!hash) return state
            if (state.dismissedEventHashes.includes(hash)) return state
            const next = [...state.dismissedEventHashes, hash]
            return {
                ...state,
                dismissedEventHashes: next.slice(-MAX_DISMISSED_HASHES),
            }
        }

        case TUTOR_EVENTS_PANEL_ACTIONS.CLEAR_DISMISSED:
            return { ...state, dismissedEventHashes: [] }

        case TUTOR_EVENTS_PANEL_ACTIONS.SYNC_STARTED_AT: {
            const { additions = {}, removals = [] } = action.payload ?? {}
            const next = { ...(state.startedAtByKey ?? {}) }
            // A removed event also retires its Sam-sent timestamp (same key).
            const nextSam = { ...(state.samSentAtByKey ?? {}) }
            removals.forEach((key) => {
                delete next[key]
                delete nextSam[key]
            })
            Object.entries(additions).forEach(([key, value]) => {
                if (!next[key]) next[key] = value
            })
            return { ...state, startedAtByKey: next, samSentAtByKey: nextSam }
        }

        case TUTOR_EVENTS_PANEL_ACTIONS.MARK_SAM_SENT: {
            const { key, at } = action.payload ?? {}
            if (!key || !at) return state
            if (state.samSentAtByKey?.[key]) return state
            return {
                ...state,
                samSentAtByKey: { ...(state.samSentAtByKey ?? {}), [key]: at },
            }
        }

        default:
            return state
    }
}

export const dismissTutorEvent = (hash) => ({
    type: TUTOR_EVENTS_PANEL_ACTIONS.DISMISS_EVENT,
    payload: { hash },
})

export const clearDismissedTutorEvents = () => ({
    type: TUTOR_EVENTS_PANEL_ACTIONS.CLEAR_DISMISSED,
})

export const syncTutorEventsStartedAt = ({ additions, removals }) => ({
    type: TUTOR_EVENTS_PANEL_ACTIONS.SYNC_STARTED_AT,
    payload: { additions, removals },
})

export const markTutorEventSamSent = (key) => ({
    type: TUTOR_EVENTS_PANEL_ACTIONS.MARK_SAM_SENT,
    payload: { key, at: Date.now() },
})
